import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { LeoRPCResult, LeoUUID } from "@surya-digital/leo-ts-types";
import { ResendEmailRPC } from "@pulse/pulse-rpcs/lib/email-cycle/resendEmailRPC";
import { getAPIClient } from "../../networking/APIClient";
import { useResendEmailRPCClientImpl } from "../rpcs/RPC";

const getInitialState = (): SnapshotIn<typeof ResendEmailStore> => ({
  loadingResendEmail: false,
  resendEmailRPCError: null,
});

export const ResendEmailStore = types
  .model("ResendEmailStore", {
    loadingResendEmail: types.boolean,
    resendEmailRPCError: types.maybeNull(
      types.enumeration(
        "ResendEmailRPCError",
        Object.values(ResendEmailRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    resendEmail: flow(function* (emailId: string) {
      store.loadingResendEmail = true;
      store.resendEmailRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const request = new ResendEmailRPC.Request(new LeoUUID(emailId));

        const result: LeoRPCResult<
          ResendEmailRPC.Response,
          ResendEmailRPC.Errors.Errors
        > = yield useResendEmailRPCClientImpl(apiClient).execute(request);

        if ("response" in result) return;

        switch (result.error.code) {
          case ResendEmailRPC.RPCError.InvalidEmailId:
          case ResendEmailRPC.RPCError.ProjectAlreadyArchived:
          case ResendEmailRPC.RPCError.ResendNotAllowed:
          case ResendEmailRPC.RPCError.UnexpectedError:
            store.resendEmailRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingResendEmail = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createResendEmailStore = (): Instance<typeof ResendEmailStore> =>
  ResendEmailStore.create(getInitialState());
